///
/// EarthBrowser
///
/// Layer Registry
///
/// Single source of truth for the Layers panel: every globe dataset with its
/// visibility binding and (when available) the command that opens its data browser.
///
/// Created by Matt Giger on 9/6/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { ComponentType, ReactNode } from "react"
import type { DatasetType } from "@/types/DatasetElement"
import { SatelliteSetChipStrip, SatelliteSetSummary } from "./SatelliteSetChips"
import type { LegendDefinition } from "@/components/legends/legendTypes"
import { AuroraLegendContent } from "@/components/legends/AuroraLegendContent"
import { BuoyLegendContent } from "@/components/legends/BuoyLegendContent"
import { EarthquakeLegendContent } from "@/components/legends/EarthquakeLegendContent"
import { HurricaneLegendContent } from "@/components/legends/HurricaneLegendContent"
import { RadarLegendContent } from "@/components/legends/RadarLegendContent"
import { SstLegendContent } from "@/components/legends/SstLegendContent"
import { SynopticLegendContent } from "@/components/legends/SynopticLegendContent"
import { WildfireLegendContent } from "@/components/legends/WildfireLegendContent"
import { Activity, Cloud, Flame, Gauge, Landmark, Layers2, Map, Mountain, Radar, Route, Satellite, Sparkles, Spline, Thermometer, TriangleAlert, Type, Video, Waves, Wind } from "lucide-react"

// Groups follow the Earth-system domain of the data (the way Worldview or
// nowCOAST catalog layers), so a user can predict where a layer lives:
// atmosphere first because it is the daily loop, then ocean, the solid
// earth and its hazards, space, and finally the set-and-forget reference
// layers. Scales to planned layers too (aircraft and ships would join
// Satellites under a "Tracking" group)
export type LayerGroup = "Atmosphere" | "Ocean" | "Earth" | "Space" | "Reference"

export const LAYER_GROUP_ORDER: LayerGroup[] = ["Atmosphere", "Ocean", "Earth", "Space", "Reference"]

export const LAYER_GROUP_LABELS: Record<LayerGroup, string> = {
	Atmosphere: "Atmosphere",
	Ocean: "Ocean",
	Earth: "Land & solid earth",
	Space: "Space",
	Reference: "Reference",
}

export interface LayerDefinition {
	id: string
	name: string
	description: string
	group: LayerGroup
	icon: ReactNode
	/**
	 * DatasetType controlling globe visibility via earthbrowser.visibleDatasets.
	 * Layers without one are table-only rows (no switch).
	 */
	visibility?: DatasetType
	/** Command palette command id that opens this layer's data browser. */
	commandId?: string
	/**
	 * Layer id of the parent layer. Sub-layers render indented under their
	 * parent and are disabled while the parent layer is hidden.
	 */
	parentId?: string
	/**
	 * Key for the globe rendering, shown in the legend tray while the layer
	 * is visible (requires `visibility`). Sub-layer keys belong in the
	 * parent's legend, as the synoptic legend does for fronts and the jet.
	 */
	legend?: LegendDefinition
	/**
	 * Per-layer controls shown under the row behind its disclosure (like
	 * sub-layers, but for choices within one layer, e.g. which satellite
	 * sets to show). `Summary` sits in the row while the strip is closed
	 */
	controls?: LayerControls
}

export interface LayerControls {
	Strip: ComponentType<{ isMobile: boolean }>
	Summary: ComponentType
}

export const LAYERS: LayerDefinition[] = [
	// Atmosphere: the daily "what's the weather doing" loop. Icon colours
	// show only while a layer is on the globe (LayerRow neutralises them
	// otherwise) and match the layer's legend accent where it has one
	{
		id: "cloud",
		name: "Clouds",
		description: "Global cloud coverage",
		group: "Atmosphere",
		icon: <Cloud className="h-4 w-4 text-slate-300" />,
		visibility: "cloud",
	},
	{
		// Continues the clouds past the newest satellite scan: ECMWF model
		// cloud worldwide with NOAA RRFS simulated GOES infrared over the US
		id: "cloud-forecast",
		name: "Cloud Forecast",
		description: "ECMWF model cloud worldwide to 3 days, with RRFS simulated satellite infrared over the US",
		group: "Atmosphere",
		icon: <Cloud className="h-4 w-4 text-slate-400" />,
		visibility: "cloud_forecast",
		parentId: "cloud",
	},
	{
		id: "precipitation",
		name: "Precipitation",
		description: "Satellite-estimated precipitation from IMERG",
		group: "Atmosphere",
		icon: <Map className="h-4 w-4 text-blue-400" />,
		visibility: "precipitation",
	},
	{
		id: "radar",
		name: "Radar",
		description: "Live reflectivity from the national weather radar networks",
		group: "Atmosphere",
		icon: <Radar className="h-4 w-4 text-emerald-400" />,
		visibility: "radar",
		legend: { chipLabel: "Radar", accent: "emerald", content: RadarLegendContent },
	},
	{
		// A rendering of the same reflectivity, not a second dataset: where a
		// NEXRAD volume draws, the flat radar steps back to a gray footprint
		id: "nexrad",
		name: "3D Storm Structure",
		description: "Volumetric NEXRAD scans replace the flat radar within range of each site (US)",
		group: "Atmosphere",
		icon: <Radar className="h-4 w-4 text-emerald-400" />,
		visibility: "nexrad",
		commandId: "radar",
		parentId: "radar",
	},
	{
		// Continues the radar timeline past the newest observation: NOAA
		// RRFS 3 km forecast reflectivity with the REFS storm probability
		id: "radar-forecast",
		name: "Radar Forecast",
		description: "RRFS forecast reflectivity out to 18+ hours over the US, with the REFS probability of strong storms",
		group: "Atmosphere",
		icon: <Radar className="h-4 w-4 text-sky-300" />,
		visibility: "radar_forecast",
		parentId: "radar",
	},
	{
		id: "synoptic",
		name: "Synoptic Weather",
		description: "Isobars and pressure centers from ECMWF",
		group: "Atmosphere",
		icon: <Gauge className="h-4 w-4 text-violet-300" />,
		visibility: "synoptic",
		legend: { chipLabel: "Synoptic", accent: "violet", content: SynopticLegendContent },
	},
	{
		id: "fronts",
		name: "Weather Fronts",
		description: "Cold, warm and stationary fronts",
		group: "Atmosphere",
		icon: <Spline className="h-4 w-4 text-violet-300" />,
		visibility: "fronts",
		parentId: "synoptic",
	},
	{
		id: "jetstream",
		name: "Jet Stream",
		description: "250 hPa winds above 50 kt, drawn at altitude",
		group: "Atmosphere",
		icon: <Wind className="h-4 w-4 text-violet-300" />,
		visibility: "jetstream",
		parentId: "synoptic",
	},
	{
		id: "upper-air",
		name: "500 hPa Heights",
		description: "Mid-level height contours: troughs and ridges",
		group: "Atmosphere",
		icon: <Layers2 className="h-4 w-4 text-violet-300" />,
		visibility: "upper_air",
		parentId: "synoptic",
	},
	{
		id: "alert",
		name: "Weather Alerts",
		description: "Official warnings from NWS, Environment Canada, and MeteoAlarm",
		group: "Atmosphere",
		icon: <TriangleAlert className="h-4 w-4 text-amber-400" />,
		visibility: "alert",
		commandId: "alerts",
	},
	{
		id: "hurricane",
		name: "Hurricanes",
		description: "Active tropical cyclones from NHC and JTWC",
		group: "Atmosphere",
		icon: <Wind className="h-4 w-4 text-sky-400" />,
		visibility: "hurricane",
		commandId: "hurricanes",
		legend: { chipLabel: "Storms", accent: "sky", content: HurricaneLegendContent },
	},

	// Ocean: sea state and temperature
	{
		id: "sst",
		name: "Sea Surface Temp",
		description: "Daily sea surface temperature from NOAA CoralTemp",
		group: "Ocean",
		icon: <Thermometer className="h-4 w-4 text-orange-400" />,
		visibility: "sst",
		legend: { chipLabel: "SST", accent: "orange", content: SstLegendContent },
	},
	{
		id: "buoy",
		name: "Buoys & Coastal Stations",
		description: "NDBC moored buoys and coastal stations: sea state, wind, pressure and water temperature",
		group: "Ocean",
		icon: <Waves className="h-4 w-4 text-cyan-400" />,
		visibility: "buoy",
		commandId: "buoys",
		legend: { chipLabel: "Buoys", accent: "cyan", content: BuoyLegendContent },
	},

	// Earth: the solid earth and the land surface, ordered by how alive
	// the data feels
	{
		id: "earthquake",
		name: "Earthquakes",
		description: "Recent seismic activity",
		group: "Earth",
		icon: <Activity className="h-4 w-4 text-red-400" />,
		visibility: "earthquake",
		commandId: "earthquakes",
		legend: { chipLabel: "Quakes", accent: "red", content: EarthquakeLegendContent },
	},
	{
		id: "volcano",
		name: "Volcanoes",
		description: "Active and historical volcanoes",
		group: "Earth",
		icon: <Mountain className="h-4 w-4 text-orange-400" />,
		visibility: "volcano",
		commandId: "volcanoes",
	},
	{
		id: "wildfire",
		name: "Wildfires",
		description: "Live fire perimeters from NIFC",
		group: "Earth",
		icon: <Flame className="h-4 w-4 text-rose-400" />,
		visibility: "wildfire",
		commandId: "wildfires",
		legend: { chipLabel: "Fires", accent: "rose", content: WildfireLegendContent },
	},

	// Space: what's above the atmosphere
	{
		id: "satellite",
		name: "Satellites",
		description: "Orbital satellite tracking",
		group: "Space",
		icon: <Satellite className="h-4 w-4 text-cyan-400" />,
		visibility: "satellite",
		commandId: "satellites",
		controls: { Strip: SatelliteSetChipStrip, Summary: SatelliteSetSummary },
	},
	{
		id: "aurora",
		name: "Aurora",
		description: "Aurora borealis visualization",
		group: "Space",
		icon: <Sparkles className="h-4 w-4 text-emerald-400" />,
		visibility: "aurora",
		legend: { chipLabel: "Aurora", accent: "emerald", content: AuroraLegendContent },
	},

	// Reference: set-and-forget display preferences and places
	{
		id: "location",
		name: "Cities & Locations",
		description: "City name labels on the globe",
		group: "Reference",
		icon: <Landmark className="h-4 w-4 text-blue-400" />,
		visibility: "location",
		commandId: "location",
	},
	{
		id: "city-weather",
		name: "Weather on Labels",
		description: "Conditions and temperature per city",
		group: "Reference",
		icon: <Thermometer className="h-4 w-4 text-amber-400" />,
		visibility: "city_weather",
		parentId: "location",
	},
	{
		id: "webcam",
		name: "Webcams",
		description: "Live public webcams",
		group: "Reference",
		icon: <Video className="h-4 w-4 text-fuchsia-400" />,
		visibility: "webcam",
		commandId: "webcams",
	},
	{
		id: "borders",
		name: "Country Borders",
		description: "Political boundaries on the globe",
		group: "Reference",
		icon: <Map className="h-4 w-4 text-emerald-400" />,
		visibility: "borders",
	},
	{
		id: "division-labels",
		name: "Country & State Names",
		description: "Name labels on political divisions",
		group: "Reference",
		icon: <Type className="h-4 w-4 text-emerald-400" />,
		visibility: "division_labels",
		parentId: "borders",
	},
	{
		id: "roads",
		name: "Roads & Highways",
		description: "Major roads with route shields",
		group: "Reference",
		icon: <Route className="h-4 w-4 text-orange-400" />,
		visibility: "roads",
	},
]
